import React, { use, useEffect, useState } from 'react';
import './ReservationPopup.css';
import { CallRounded, ChatBubbleRounded, CloseRounded, MessageRounded } from '@mui/icons-material';
import { supabase, assignCleanerToReservation, removeCleanerAssignment } from '../supabase';

function ReservationPopup({
    reservationData, 
    showEntireReservation, 
    setShowEntireReservation,
    cleanerAssignments, // Add this new prop to receive assignments
    onCleanerAssigned // Add this callback prop to notify parent of changes
}) {
    console.log('ReservationPopup rendered with props:', { 
        reservationData, 
        showEntireReservation
    });
    
    const [occurenceOfReservation, setOccurenceOfReservation] = useState('');
    // Add states for cleaner assignment
    const [cleaners, setCleaners] = useState([]);
    const [selectedCleaner, setSelectedCleaner] = useState(null);
    const [loading, setLoading] = useState(false);
    const [assignmentMessage, setAssignmentMessage] = useState(null);
    
    const now = new Date();

    // Fetch cleaners from the database
    useEffect(() => {
        const fetchCleaners = async () => {
            const { data, error } = await supabase
                .from('cleaners')
                .select('*')
                .order('first_name', { ascending: true });
            
            if (error) {
                console.error('Error fetching cleaners:', error.message);
                return;
            }
            
            setCleaners(data || []);
        };
        
        fetchCleaners();
    }, []);
    
    // Set selected cleaner from passed-down assignments instead of fetching
    useEffect(() => {
        if (!reservationData?.reservations?.id || !cleanerAssignments) return;
        
        const reservationId = reservationData.reservations.id;
        const assignment = cleanerAssignments[reservationId];
        
        console.log('ReservationPopup processing assignment change:', {
            reservationId,
            assignment,
            allAssignments: cleanerAssignments
        });
        
        if (assignment) {
            setSelectedCleaner(assignment.id); // Assuming cleaner object has id
        } else {
            setSelectedCleaner(null); // Ensure we clear selection if assignment is removed elsewhere
        }
    }, [reservationData?.reservations?.id, cleanerAssignments]);

    // Handle assigning a cleaner
    const handleCleanerAssignment = async (cleanerId) => {
        if (!reservationData?.reservations?.id) return;
        
        setLoading(true);
        setAssignmentMessage(null);
        
        try {
            if (cleanerId) {
                // Update local state immediately for better UX
                setSelectedCleaner(cleanerId);
                
                // Get the cleaner object from the cleaners array
                const assignedCleaner = cleaners.find(c => c.id === cleanerId);
                
                // Notify parent component of the assignment change immediately
                if (onCleanerAssigned && assignedCleaner) {
                    onCleanerAssigned(reservationData.reservations.id, assignedCleaner);
                }
                
                // Then update the database
                await assignCleanerToReservation(reservationData.reservations.id, cleanerId);
                setAssignmentMessage({ type: 'success', text: 'Cleaner assigned successfully' });
            } else {
                // Update local state immediately for better UX
                setSelectedCleaner(null);
                
                // Notify parent component that the assignment was removed immediately
                if (onCleanerAssigned) {
                    onCleanerAssigned(reservationData.reservations.id, null);
                }
                
                // Then update the database
                await removeCleanerAssignment(reservationData.reservations.id);
                setAssignmentMessage({ type: 'info', text: 'Cleaner assignment removed' });
            }
            
            // Clear message after 3 seconds
            setTimeout(() => {
                setAssignmentMessage(null);
            }, 3000);
        } catch (error) {
            console.error('Error assigning cleaner:', error);
            setAssignmentMessage({ type: 'error', text: 'Failed to assign cleaner' });
            
            // If there was an error, revert the local state by reloading from props
            if (reservationData?.reservations?.id && cleanerAssignments) {
                const assignment = cleanerAssignments[reservationData.reservations.id];
                setSelectedCleaner(assignment ? assignment.id : null);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Add a check if reservationData is null or undefined
        if (!reservationData || !reservationData.reservations) {
            console.error('No reservation data provided to ReservationPopup');
            return;
        }
        
        const now = new Date();
        const reservation_date = new Date(reservationData?.reservations?.reservation_date || now);
        const arrival_date = new Date(reservationData?.reservations?.arrival_date || now);
        const departure_date = new Date(reservationData?.reservations?.departure_date || now);
    
    
        if (arrival_date > now && departure_date > now) {
            const totalTimeFromReservationToArrival = arrival_date - reservation_date;
            const elapsedTimeFromReservation = now - reservation_date;

            const percentCompleted = ((Math.min(elapsedTimeFromReservation, totalTimeFromReservationToArrival) / totalTimeFromReservationToArrival) * 100).toFixed(2);
            console.log("This reservation is in the future");
            setOccurenceOfReservation({
                direction: "future",
                beforeCompleted: percentCompleted, 
                duringCompleted: 0,
                afterCompleted: 0
            });
        } else if (arrival_date < now && departure_date > now) {

            const totalDuration = departure_date - arrival_date;
            const elapsedTime = now - arrival_date;
            const percentCompleted = ((elapsedTime / totalDuration) * 100).toFixed(2);
            
            console.log(`This reservation is current. ${percentCompleted}% of the stay has been completed.`);
            setOccurenceOfReservation({
                direction: "current",
                beforeCompleted: 100,  
                duringCompleted: percentCompleted,
                afterCompleted: 0
            });
        } else if (arrival_date < now && departure_date < now) {
            console.log("This reservation is in the past");
    
            
            setOccurenceOfReservation({
                direction: "past",
                beforeCompleted: 100, 
                duringCompleted: 100,
                afterCompleted: 100
            });
        } 
    }, [reservationData, showEntireReservation]);
    

    


 

    
    
    

    function formatDate(inputDate) {
        const date = new Date(inputDate);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    
    const formatTime = (hour) => {
        if (hour < 0 || hour > 23 || isNaN(hour)) return "Invalid hour";
        
        const period = hour >= 12 ? "PM" : "AM";
        const formattedHour = hour % 12 || 12; // Converts 0 to 12, 13 to 1, etc.
        
        return `${formattedHour}:00 ${period}`;
    };

    

    if (!showEntireReservation) { 
        console.log('Not showing popup because showEntireReservation is false');
        return null; 
    }

    // Add additional safeguard to prevent rendering with invalid data
    if (!reservationData || !reservationData.reservations) {
        console.error('Cannot render ReservationPopup without valid reservationData');
        return null;
    }

    // Find the selected cleaner object
    const selectedCleanerObj = selectedCleaner ? cleaners.find(c => c.id === selectedCleaner) : null;

    console.log('Rendering popup content');
    return (
        <div className='ReservationPopupContainer'>
            <div className='ReservationPopupContent'>
                <div className='ReservationPopupCloseButton' onClick={() => setShowEntireReservation(false)}>
                    <CloseRounded style={{width: 20, height: 20, color: '#666'}}/>
                </div>

                <div className='ReservationPopupHeaderRow'>
                    <div className='ReservationPopupHeaderRowLeft'>
                        <img src={reservationData?.recipient_picture} className='ReservationPopupProfilePicture'/>
                    </div>
                    <div className='ReservationPopupHeaderRowRight'>
                        <div className='ReservationPopupStatus'>{reservationData?.reservations?.status}</div>
                        <div className='ReservationPopupName'>{reservationData?.recipient_name}</div>
                        <div className='ReservationPopupListingName'>{reservationData?.listings?.internal_listing_name} · {reservationData?.reservations?.number_of_guests} guests · {reservationData?.reservations?.pets || 0} pets · {new Intl.NumberFormat('en-US', {style: 'currency',currency: 'USD',}).format(reservationData?.reservations?.total_price)}</div>
                        
                        {reservationData?.reservations?.status != 'inquiry' && (
                        <div className='ReservationPopupContactContainer'>
                            <a style={{textDecoration: 'none', color: '#000'}} href={`tel:${reservationData?.reservations?.phone}`}>
                                <div className='ReservationPopupContactButton'>
                                    {/* <div className='ReservationPopupContactButtonIcon'><CallRounded style={{height: 20, width: 20, color: '#FD60B9FF'}}/></div> */}
                                    <div className='ReservationPopupContactButtonTitle'>Call</div>
                                </div>
                            </a>
                            <a style={{textDecoration: 'none', color: '#000'}} href={`sms:+1${reservationData?.reservations?.phone}`}>
                                <div className='ReservationPopupContactButton'>
                                    {/* <div className='ReservationPopupContactButtonIcon'><MessageRounded style={{height: 20, width: 20, color: '#50B7FFFF'}}/></div> */}
                                    <div className='ReservationPopupContactButtonTitle'>SMS</div>
                                </div>
                            </a>
                            <div className='ReservationPopupContactButton'>
                                {/* <div className='ReservationPopupContactButtonIcon'><ChatBubbleRounded style={{height: 20, width: 20, color: '#A967D5FF'}}/></div> */}
                                <div className='ReservationPopupContactButtonTitle'>Message</div>
                            </div>
                            <a style={{textDecoration: 'none', color: '#000'}} href={reservationData?.reservations?.guest_portal_url}>
                                <div className='ReservationPopupContactButton'>
                                    {/* <div className='ReservationPopupContactButtonIcon'><ChatBubbleRounded style={{height: 20, width: 20, color: '#A967D5FF'}}/></div> */}
                                    <div className='ReservationPopupContactButtonTitle'>Portal</div>
                                </div>
                            </a>
                        </div>
                        )}
                    </div>
                </div>

                {/* Add cleaner assignment section */}
                <div className='ReservationPopupCleanerSection'>
                    <h3 className='ReservationPopupSectionTitle'>Cleaner Assignment</h3>
                    
                    <div className='ReservationPopupCleanerContainer'>
                        {selectedCleanerObj && (
                            <div className='ReservationPopupSelectedCleaner'>
                                <div 
                                    className='ReservationPopupCleanerColor' 
                                    style={{ 
                                        backgroundColor: selectedCleanerObj.color.startsWith('#') 
                                            ? selectedCleanerObj.color 
                                            : `#${selectedCleanerObj.color}` 
                                    }}
                                >
                                    <span>{selectedCleanerObj.initials}</span>
                                </div>
                                <div className='ReservationPopupCleanerName'>
                                    {selectedCleanerObj.first_name} {selectedCleanerObj.last_name}
                                </div>
                                <button 
                                    className='ReservationPopupCleanerRemoveButton'
                                    onClick={() => handleCleanerAssignment(null)}
                                    disabled={loading}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                        
                        {!selectedCleanerObj && (
                            <div className='ReservationPopupCleanerSelector'>
                                <select 
                                    className='ReservationPopupCleanerDropdown'
                                    value=""
                                    onChange={(e) => handleCleanerAssignment(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="" disabled>Select a cleaner</option>
                                    {cleaners.map(cleaner => (
                                        <option key={cleaner.id} value={cleaner.id}>
                                            {cleaner.first_name} {cleaner.last_name} ({cleaner.initials})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        
                        {assignmentMessage && (
                            <div className={`ReservationPopupMessage ${assignmentMessage.type}`}>
                                {assignmentMessage.text}
                            </div>
                        )}
                    </div>
                </div>
               



                <div className='ReservationPopupProgressContainer'>
                    <div className='ReservationPopupProgressDirection'>{occurenceOfReservation.direction == 'past' ? "After Stay" : occurenceOfReservation.direction == 'future' ? "Before Stay" : "Current Stay"}</div>
                    <div className='ReservationPopupDates'>{formatDate(reservationData.reservations?.arrival_date)} – {formatDate(reservationData.reservations?.departure_date)}</div>

                    <div className='ReservationPopupProgressBarsGrid'>
                        <div className='ReservationPopupProgressBarsGridSection'>
                            <div className='ReservationPopupProgressBarsGridProgressBar'>
                                <div className='ReservationPopupProgressBarsGridProgressBarFill' style={{width: `${occurenceOfReservation.beforeCompleted}%`}}></div>
                            </div>
                            <div className='ReservationPopupProgressBarsGridProgressBarSectionTitle'>Before</div>
                        </div>
                        <div className='ReservationPopupProgressBarsGridSection'>
                            <div className='ReservationPopupProgressBarsGridProgressBar'>
                                <div className='ReservationPopupProgressBarsGridProgressBarFill' style={{width: `${occurenceOfReservation.duringCompleted}%`}}></div>
                            </div>
                            <div className='ReservationPopupProgressBarsGridProgressBarSectionTitle'>During</div>
                        </div>
                        <div className='ReservationPopupProgressBarsGridSection'>
                            <div className='ReservationPopupProgressBarsGridProgressBar'>
                                <div className='ReservationPopupProgressBarsGridProgressBarFill' style={{width: `${occurenceOfReservation.afterCompleted}%`}}></div>
                            </div>
                            <div className='ReservationPopupProgressBarsGridProgressBarSectionTitle'>After</div>
                        </div>
                    </div>
                </div>

                <div className='ReservationPopupHighlightsContainer'>
                    <div className='ReservationPopupHighlightsDetailsTag'>
                            <div className='ReservationPopupHighlightsDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.payment_status == 'Paid' && '#00b000'}}></div>
                            <div className='ReservationPopupHighlightsDetailsTagTitle'>Paid</div>
                        </div>
                        <div className='ReservationPopupHighlightsDetailsTag'>
                            <div className='ReservationPopupHighlightsDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.stripe_guest_id && '#00b000'}}></div>
                            <div className='ReservationPopupHighlightsDetailsTagTitle'>Card Added</div>
                        </div>

                        <div className='ReservationPopupHighlightsDetailsTag'>
                            <div className='ReservationPopupHighlightsDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.stripe_guest_id && '#00b000'}}></div>
                            <div className='ReservationPopupHighlightsDetailsTagTitle'>Deposit Paid</div>
                        </div>
                        {reservationData?.reservations?.early_check_in_paid && (
                        <div className='ReservationPopupHighlightsDetailsTag'>
                            <div className='ReservationPopupHighlightsDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.early_check_in_paid && '#00b000'}}></div>
                            <div className='ReservationPopupHighlightsDetailsTagTitle'>{formatTime(reservationData?.reservations?.early_check_in_time)} Arrival</div>
                        </div>
                        )}
                        {reservationData?.reservations?.eta && (
                        <div className='ReservationPopupHighlightsDetailsTag'>
                            <div className='ReservationPopupHighlightsDetailsTagDot' style={{backgroundColor: '#00b000'}}></div>
                            <div className='ReservationPopupHighlightsDetailsTagTitle'>{formatTime(reservationData?.reservations?.eta)} ETA</div>
                        </div>
                        )}
                        {reservationData?.reservations?.etd && (
                        <div className='ReservationPopupHighlightsDetailsTag'>
                            <div className='ReservationPopupHighlightsDetailsTagDot' style={{backgroundColor: '#00b000'}}></div>
                            <div className='ReservationPopupHighlightsDetailsTagTitle'>{formatTime(reservationData?.reservations?.etd)} ETD</div>
                        </div>
                        )}
                        {reservationData?.reservations?.checked_out && (
                        <div className='ReservationPopupHighlightsDetailsTag'>
                            <div className='ReservationPopupHighlightsDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.checked_out && '#00b000'}}></div>
                            <div className='ReservationPopupHighlightsDetailsTagTitle'>Departed</div>
                        </div>
                        )}
                        {reservationData?.reservations?.late_check_out_paid && (
                        <div className='ReservationPopupHighlightsDetailsTag'>
                            <div className='ReservationPopupHighlightsDetailsTagDot' style={{backgroundColor: reservationData?.reservations?.late_check_out_paid && '#00b000'}}></div>
                            <div className='ReservationPopupHighlightsDetailsTagTitle'>{formatTime(reservationData?.reservations?.late_check_out_time)} Departure</div>
                        </div>
                        )}

                </div>

                <div className='ReservationPopupDetailsSection'>
                    <h3 className='ReservationPopupDetailsSectionTitle'>Reservation Details</h3>
                    
                    <div className='ReservationPopupDetailsCard'>
                        <div className='ReservationPopupDetailsCardItem'>
                            <div className='ReservationPopupDetailsCardLabel'>Reservation Date</div>
                            <div className='ReservationPopupDetailsCardValue'>
                                {reservationData?.reservations?.reservation_date && 
                                    new Date(reservationData.reservations.reservation_date).toLocaleDateString('en-US', {
                                        year: 'numeric', 
                                        month: 'short', 
                                        day: 'numeric'
                                    })
                                }
                            </div>
                        </div>

                        {reservationData?.reservations?.guest_rating && (
                            <div className='ReservationPopupDetailsCardItem'>
                                <div className='ReservationPopupDetailsCardLabel'>Guest Rating</div>
                                <div className='ReservationPopupDetailsCardValue'>
                                    <div className='ReservationPopupRating'>
                                        {Array.from({ length: 5 }).map((_, index) => (
                                            <span 
                                                key={index} 
                                                className='ReservationPopupRatingStar'
                                                style={{ color: index < reservationData.reservations.guest_rating ? '#FFD700' : '#ddd' }}
                                            >
                                                ★
                                            </span>
                                        ))}
                                        <span className='ReservationPopupRatingValue'>
                                            {reservationData.reservations.guest_rating}/5
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {reservationData?.reservations?.conversation_notes && (
                        <div className='ReservationPopupDetailsCard'>
                            <div className='ReservationPopupDetailsCardLabel'>Conversation Notes</div>
                            <div className='ReservationPopupDetailsCardValue ReservationPopupNotes'>
                                {reservationData.reservations.conversation_notes}
                            </div>
                        </div>
                    )}

                    {reservationData?.reservations?.conversation_summary && (
                        <div className='ReservationPopupDetailsCard'>
                            <div className='ReservationPopupDetailsCardLabel'>Conversation Summary</div>
                            <div className='ReservationPopupDetailsCardValue ReservationPopupNotes'>
                                {reservationData.reservations.conversation_summary}
                            </div>
                        </div>
                    )}
                </div>
                   
            </div>
        </div>
    )
}

export default ReservationPopup;